import styled from 'styled-components';
import { TextKey } from '@mews-commander/enums';
import { LocalizedEnumify, LocalizedText } from '@mews/framework';
import {
    ConvertedCurrencyValue,
    DescriptionItem,
    Loader,
    LocalizedDateTimeFromUtc,
    LocalizedDayNameFromUtc,
    TypographyOptions,
} from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { isUnconfirmedState } from '@mews-commander/types';
import { ReservationPurpose, CancellationReason } from '@mews-commander/legacy-data-structures';
import { getCurrentEnterpriseReservation } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getReservationGroupName } from '@mews-commander/legacy-entities/src/ducks/reservationGroups';
import { useCommanderSelector } from '@mews-commander/utils';
import { ServiceName, RateName, ResourceCategoryName } from '../../../../components/commander-ui';
import { CompanyLink, TravelAgencyLink } from '../Navigation';
import { useAvailabilityBlock } from '../../../availabilityBlock/utils/useAvailabilityBlock';
import { VoucherName } from '../VoucherName';
import { ReservationStateName } from './ReservationStateName';
import BookerLabel from './BookerLabel';
import CompanionLabel from './CompanionLabel';
import AssignedResource from './AssignedResource';
import PaymentCardInfo from './PaymentCardInfo';
import { NotesList } from './NotesList';
import BusinessSegmentName from './BusinessSegmentName';
import AssignedResourceCategory from './AssignedResourceCategory';
import ReservationOrderItems from './ReservationOrderItems';
import ReservationPricing from './ReservationPricing';

export const ReservationInfoWrapper = styled.div`
    overflow: auto;
`;

interface ReservationInfoProps {
    scope: string;
    reservationId: string;
    refetchGroup: () => void;
    isFetching: boolean;
}

const ReservationInfo = ({ reservationId, scope, refetchGroup, isFetching }: ReservationInfoProps) => {
    const isMultiTaxEnvironment: boolean = useCommanderSelector(isCurrentEnterpriseInMultiTaxEnvironment);
    const groupName = useCommanderSelector((state) => getReservationGroupName(state, { id: scope }));
    const reservation = useCommanderSelector((state) => getCurrentEnterpriseReservation(state, { id: reservationId }));
    const availabilityBlock = useAvailabilityBlock(reservation?.availabilityBlockId);

    if (!reservation) {
        return <Loader />;
    }

    const {
        id,
        startUtc,
        startedUtc,
        endUtc,
        releasedUtc,
        createdUtc,
        reservationTimeState,
        options,
        serviceOrderState,
        cancellationReason,
        purpose,
        businessSegmentId,
        bookerId,
        assignedResourceId,
        partnerCompanyId,
        rateId,
        appliedVoucherId,
        travelAgencyId,
        requestedCategoryId,
        number,
        channelNumber,
        serviceId,
        origin,
        originDetails,
        source,
        preCancelTotalAmount,
        creditCardId,
    } = reservation;

    return (
        <div data-test-id="reservationInfo">
            <ReservationInfoWrapper>
                <DescriptionItem
                    data-test-id="serviceId"
                    term={<LocalizedText textKey={TextKey.Service} />}
                    definition={<ServiceName id={serviceId} />}
                />
                <DescriptionItem
                    data-test-id="confirmationNumber"
                    term={<LocalizedText textKey={TextKey.ConfirmationNumber} />}
                    definition={number}
                />
                {groupName ? (
                    <DescriptionItem
                        data-test-id="groupName"
                        term={<LocalizedText textKey={TextKey.GroupName} />}
                        definition={
                            <TypographyOptions wordBreak="break-all" data-test-id="groupNameValue">
                                {groupName}
                            </TypographyOptions>
                        }
                    />
                ) : null}
                {availabilityBlock && (
                    <DescriptionItem
                        data-test-id="availabilityBlock"
                        term={<LocalizedText textKey={TextKey.AvailabilityBlock} />}
                        definition={availabilityBlock.name}
                    />
                )}
                <DescriptionItem
                    data-test-id="reservationStatus"
                    term={<LocalizedText textKey={TextKey.ReservationStatus} />}
                    definition={
                        <ReservationStateName
                            reservationTimeState={reservationTimeState}
                            serviceOrderState={serviceOrderState}
                            options={options}
                        />
                    }
                />
                {bookerId ? (
                    <DescriptionItem
                        data-test-id="booker"
                        term={<LocalizedText textKey={TextKey.Booker} />}
                        definition={<BookerLabel bookerId={bookerId} refetchGroup={refetchGroup} />}
                    />
                ) : null}
                {cancellationReason ? (
                    <DescriptionItem
                        data-test-id="cancellationReason"
                        term={<LocalizedText textKey={TextKey.CancellationReason} />}
                        definition={
                            <LocalizedEnumify
                                enumValue={CancellationReason.getByName(cancellationReason)}
                                data-test-id="cancellationReasonValue"
                            />
                        }
                    />
                ) : null}
                <DescriptionItem
                    data-test-id="arrival"
                    term={<LocalizedText textKey={TextKey.Arrival} />}
                    definition={
                        <>
                            <LocalizedDateTimeFromUtc utcString={startedUtc || startUtc} />
                            {UnicodeEntity.NoBreakSpace}
                            <LocalizedDayNameFromUtc utcString={startedUtc || startUtc} />
                        </>
                    }
                />
                <DescriptionItem
                    data-test-id="departure"
                    term={<LocalizedText textKey={TextKey.Departure} />}
                    definition={
                        <>
                            <LocalizedDateTimeFromUtc utcString={endUtc} />
                            {UnicodeEntity.NoBreakSpace}
                            <LocalizedDayNameFromUtc utcString={endUtc} />
                        </>
                    }
                />
                {isUnconfirmedState(serviceOrderState) && releasedUtc ? (
                    <DescriptionItem
                        data-test-id="release"
                        term={<LocalizedText textKey={TextKey.Release} />}
                        definition={<LocalizedDateTimeFromUtc utcString={releasedUtc} />}
                    />
                ) : null}
                {purpose ? (
                    <DescriptionItem
                        data-test-id="reservationPurpose"
                        term={<LocalizedText textKey={TextKey.ReservationPurpose} />}
                        definition={
                            <LocalizedEnumify
                                enumValue={ReservationPurpose.getByValue(purpose)}
                                data-test-id="reservationPurposeValue"
                            />
                        }
                    />
                ) : null}
                {businessSegmentId ? (
                    <DescriptionItem
                        data-test-id="segment"
                        term={<LocalizedText textKey={TextKey.Segment} />}
                        definition={<BusinessSegmentName businessSegmentId={businessSegmentId} />}
                    />
                ) : null}
                <DescriptionItem
                    data-test-id="companions"
                    term={<LocalizedText textKey={TextKey.CompanionPlural} />}
                    definition={<CompanionLabel reservationId={id} scope={scope} refetchGroup={refetchGroup} />}
                />
            </ReservationInfoWrapper>

            <ReservationInfoWrapper>
                <ReservationPricing reservationId={id} serviceId={serviceId} scope={scope} isFetching={isFetching} />
            </ReservationInfoWrapper>

            <ReservationInfoWrapper>
                {preCancelTotalAmount ? (
                    <DescriptionItem
                        definitionHighlighted
                        data-test-id="canceledCost"
                        term={<LocalizedText textKey={TextKey.CanceledCost} />}
                        definition={
                            <ConvertedCurrencyValue amount={preCancelTotalAmount} net={isMultiTaxEnvironment} />
                        }
                    />
                ) : null}
                <DescriptionItem
                    data-test-id="requestedResourceCategory"
                    term={<LocalizedText textKey={TextKey.RequestedCategory} />}
                    definition={<ResourceCategoryName id={requestedCategoryId} serviceId={serviceId} />}
                />
                <AssignedResourceCategory
                    serviceId={serviceId}
                    assignedResourceId={assignedResourceId}
                    requestedCategoryId={requestedCategoryId}
                />
                <DescriptionItem
                    data-test-id="assignedSpace"
                    definitionHighlighted
                    term={<LocalizedText textKey={TextKey.AssignedSpace} />}
                    definition={
                        <AssignedResource
                            assignedResourceId={assignedResourceId}
                            requestedCategoryId={requestedCategoryId}
                            serviceId={serviceId}
                            reservationId={id}
                            serviceOrderState={serviceOrderState}
                        />
                    }
                />
                <DescriptionItem
                    data-test-id="rate"
                    term={<LocalizedText textKey={TextKey.Rate} />}
                    definition={<RateName rateId={rateId} />}
                />
                {appliedVoucherId ? (
                    <DescriptionItem
                        data-test-id="voucher"
                        term={<LocalizedText textKey={TextKey.Voucher} />}
                        definition={<VoucherName id={appliedVoucherId} />}
                    />
                ) : null}
                {partnerCompanyId ? (
                    <DescriptionItem
                        data-test-id="company"
                        term={<LocalizedText textKey={TextKey.Company} />}
                        definition={<CompanyLink id={partnerCompanyId} />}
                    />
                ) : null}
                {travelAgencyId ? (
                    <DescriptionItem
                        data-test-id="travelAgency"
                        term={<LocalizedText textKey={TextKey.TravelAgency} />}
                        definition={<TravelAgencyLink id={travelAgencyId} />}
                    />
                ) : null}
                {channelNumber ? (
                    <DescriptionItem
                        data-test-id="travelAgencyConfirmationNumber"
                        term={<LocalizedText textKey={TextKey.TravelAgencyConfirmationNumber} />}
                        definition={channelNumber}
                    />
                ) : null}
                <ReservationOrderItems reservationId={id} serviceId={serviceId} scope={scope} isFetching={isFetching} />
                {creditCardId ? (
                    <DescriptionItem
                        data-test-id="paymentCard"
                        term={<LocalizedText textKey={TextKey.PaymentCard} />}
                        definition={<PaymentCardInfo paymentCardId={creditCardId} />}
                    />
                ) : null}
                <DescriptionItem
                    data-test-id="origin"
                    term={<LocalizedText textKey={TextKey.Origin} />}
                    definition={origin}
                />
                {originDetails ? (
                    <DescriptionItem
                        data-test-id="reservationOriginDetails"
                        term={<LocalizedText textKey={TextKey.ReservationOriginDetails} />}
                        definition={originDetails}
                    />
                ) : null}
                {source ? (
                    <DescriptionItem
                        data-test-id="reservationSource"
                        term={<LocalizedText textKey={TextKey.ReservationSource} />}
                        definition={source}
                    />
                ) : null}
                <DescriptionItem
                    data-test-id="created"
                    term={<LocalizedText textKey={TextKey.Created} />}
                    definition={<LocalizedDateTimeFromUtc utcString={createdUtc} />}
                />
                <NotesList scope={scope} serviceOrderId={id} />
            </ReservationInfoWrapper>
        </div>
    );
};

export default ReservationInfo;
